import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Megaphone,
  Sparkles,
  Send,
  Trash2,
  RefreshCw,
  Linkedin,
  Facebook,
  Instagram,
  Plus,
  Loader2,
  Calendar,
  CalendarDays,
  Edit3,
  X,
  CheckCircle2,
  AlertCircle,
  Clock,
  FileText,
  Upload,
  Image as ImageIcon,
  Play,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Link2,
  Settings,
  Eye,
  EyeOff,
  Save,
} from 'lucide-react';
import {
  marketingApi,
  type MarketingPost,
  type MarketingFeature,
  type MarketingAccount,
  type MarketingPlatform,
  type MarketingContentType,
  type MarketingTargetAudience,
  type MarketingGeneratedPost,
  type MarketingThemeModule,
  type MarketingThemeModuleKey,
} from '../../services/api';
import { useAuthStore } from '../../store/auth.store';

type Tab = 'posts' | 'compose' | 'features' | 'accounts' | 'calendar';

const PLATFORMS: { value: MarketingPlatform; label: string; icon: typeof Linkedin; color: string }[] = [
  { value: 'LINKEDIN', label: 'LinkedIn', icon: Linkedin, color: '#0A66C2' },
  { value: 'FACEBOOK', label: 'Facebook', icon: Facebook, color: '#1877F2' },
  { value: 'INSTAGRAM', label: 'Instagram', icon: Instagram, color: '#E4405F' },
];

const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  SCHEDULED: 'bg-blue-100 text-blue-700',
  PUBLISHING: 'bg-yellow-100 text-yellow-700',
  PUBLISHED: 'bg-green-100 text-green-700',
  PARTIAL: 'bg-orange-100 text-orange-700',
  FAILED: 'bg-red-100 text-red-700',
};

const STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Brouillon',
  SCHEDULED: 'Programmé',
  PUBLISHING: 'En cours…',
  PUBLISHED: 'Publié',
  PARTIAL: 'Partiel',
  FAILED: 'Échec',
};

const DAY_NAMES = ['lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.', 'dim.'];

function getMonday(d: Date): Date {
  const date = new Date(d);
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diff);
  date.setHours(0, 0, 0, 0);
  return date;
}

function formatDayHeader(d: Date, idx: number): string {
  return `${DAY_NAMES[idx]} ${d.getDate()} ${d.toLocaleDateString('fr-FR', { month: 'short' })}`;
}

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function isToday(d: Date): boolean {
  return isSameDay(d, new Date());
}

const ACCEPTED_MEDIA_TYPES = '.jpg,.jpeg,.png,.gif,.webp,.mp4';
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

function isVideoUrl(url: string): boolean {
  return /\.mp4$/i.test(url);
}

export default function MarketingPage() {
  const [tab, setTab] = useState<Tab>('posts');
  const [posts, setPosts] = useState<MarketingPost[]>([]);
  const [publishingId, setPublishingId] = useState<string | null>(null);
  const [features, setFeatures] = useState<MarketingFeature[]>([]);
  const [featureScrapeOpen, setFeatureScrapeOpen] = useState(false);
  const [featureScrapeUrl, setFeatureScrapeUrl] = useState('');
  const [featureScrapeLoading, setFeatureScrapeLoading] = useState(false);
  const [featureScrapeResults, setFeatureScrapeResults] = useState<
    Array<{
      slug: string;
      title: string;
      description: string;
      category?: string;
      sourceUrl?: string;
      exists: boolean;
    }>
  >([]);
  const [featureScrapeSelected, setFeatureScrapeSelected] = useState<Set<string>>(new Set());
  const [featureFormOpen, setFeatureFormOpen] = useState(false);
  const [featureFormEditingId, setFeatureFormEditingId] = useState<string | null>(null);
  const [featureFormTitle, setFeatureFormTitle] = useState('');
  const [featureFormDescription, setFeatureFormDescription] = useState('');
  const [featureFormCategory, setFeatureFormCategory] = useState('');
  const [featureFormSourceUrl, setFeatureFormSourceUrl] = useState('');
  const [featureFormSaving, setFeatureFormSaving] = useState(false);
  const [accounts, setAccounts] = useState<MarketingAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // OAuth credentials state
  const [credentialsOpen, setCredentialsOpen] = useState(false);
  const [settingsStatus, setSettingsStatus] = useState<Record<string, { configured: boolean; source: string }>>({});
  const [settingsMasked, setSettingsMasked] = useState<Record<string, string | null>>({});
  const [credentialInputs, setCredentialInputs] = useState<Record<string, string>>({});
  const [credentialSaving, setCredentialSaving] = useState<string | null>(null);
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Composer state
  const [editingPost, setEditingPost] = useState<MarketingPost | null>(null);
  const [composerTitle, setComposerTitle] = useState('');
  const [composerContent, setComposerContent] = useState('');
  const [composerPlatforms, setComposerPlatforms] = useState<MarketingPlatform[]>(['LINKEDIN']);
  const [composerSchedule, setComposerSchedule] = useState('');
  const [composerSaving, setComposerSaving] = useState(false);
  const [composerMediaUrls, setComposerMediaUrls] = useState<string[]>([]);
  const [mediaUploading, setMediaUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // AI assist state
  const [aiTheme, setAiTheme] = useState('');
  const [aiTone, setAiTone] = useState<'professional' | 'casual' | 'enthusiastic' | 'technical'>(
    'professional',
  );
  const [aiLanguage, setAiLanguage] = useState<'fr' | 'en' | 'de'>('fr');
  const [aiContentType, setAiContentType] = useState<MarketingContentType>('product-highlight');
  const [aiTargetAudience, setAiTargetAudience] = useState<MarketingTargetAudience | ''>('');
  const [aiCta, setAiCta] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  // Module theme catalogue (ready-made AI angles per product module)
  const [themeModules, setThemeModules] = useState<MarketingThemeModule[]>([]);
  const [aiModuleKey, setAiModuleKey] = useState<MarketingThemeModuleKey | ''>('');

  // Calendar generator state (compose tab)
  const [calendarTheme, setCalendarTheme] = useState('');
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarPosts, setCalendarPosts] = useState<MarketingGeneratedPost[]>([]);
  const [calendarSelected, setCalendarSelected] = useState<Set<number>>(new Set());
  const [calendarModuleKey, setCalendarModuleKey] = useState<MarketingThemeModuleKey | ''>('');
  const aiModule = useMemo(
    () => themeModules.find((m) => m.key === aiModuleKey),
    [themeModules, aiModuleKey],
  );

  // Visual calendar state
  const [calendarWeekStart, setCalendarWeekStart] = useState(() => getMonday(new Date()));
  const [calendarAllPosts, setCalendarAllPosts] = useState<MarketingPost[]>([]);
  const [calendarViewLoading, setCalendarViewLoading] = useState(false);

  // Check URL params for OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get('connected');
    const tabParam = params.get('tab');
    if (tabParam === 'accounts' && connected) {
      setTab('accounts');
      setSuccessMsg(`Compte ${connected} connecté avec succès !`);
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
      setTimeout(() => setSuccessMsg(null), 5000);
    }
  }, []);

  const loadPosts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setPosts(await marketingApi.listPosts({ limit: 100 }));
    } catch (e) {
      setError('Erreur de chargement des posts');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadFeatures = useCallback(async () => {
    setLoading(true);
    try {
      setFeatures(await marketingApi.listFeatures());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadThemeModules = useCallback(async () => {
    try {
      setThemeModules(await marketingApi.listThemes());
    } catch (e) {
      console.error(e);
    }
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      const data = await marketingApi.getSettings();
      setSettingsStatus(data.status);
      setSettingsMasked(data.masked);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const loadAccounts = useCallback(async () => {
    setLoading(true);
    try {
      setAccounts(await marketingApi.listAccounts());
      await loadSettings();
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [loadSettings]);

  const loadCalendarPosts = useCallback(async () => {
    setCalendarViewLoading(true);
    try {
      setCalendarAllPosts(await marketingApi.listPosts({ limit: 200 }));
    } catch (e) {
      console.error(e);
    } finally {
      setCalendarViewLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'posts') loadPosts();
    if (tab === 'features') loadFeatures();
    if (tab === 'accounts') loadAccounts();
    if (tab === 'calendar') loadCalendarPosts();
    // Compose tab needs the features list to feed the "inspire from a feature" picker
    if (tab === 'compose' && features.length === 0) loadFeatures();
    // …and the module theme catalogue for the "theme per module" pickers
    if (tab === 'compose' && themeModules.length === 0) loadThemeModules();
  }, [
    tab,
    loadPosts,
    loadFeatures,
    loadAccounts,
    loadCalendarPosts,
    loadThemeModules,
    features.length,
    themeModules.length,
  ]);

  const resetComposer = () => {
    setEditingPost(null);
    setComposerTitle('');
    setComposerContent('');
    setComposerPlatforms(['LINKEDIN']);
    setComposerSchedule('');
    setComposerMediaUrls([]);
    setAiTheme('');
    setAiModuleKey('');
  };

  const openComposerForEdit = (p: MarketingPost) => {
    setEditingPost(p);
    setComposerTitle(p.title || '');
    setComposerContent(p.content);
    setComposerPlatforms(p.platforms);
    setComposerSchedule(p.scheduledAt ? p.scheduledAt.slice(0, 16) : '');
    setComposerMediaUrls(p.mediaUrls || []);
    setTab('compose');
  };

  const openComposerForDate = (date: Date) => {
    resetComposer();
    const local = new Date(date);
    local.setHours(9, 0, 0, 0);
    const year = local.getFullYear();
    const month = String(local.getMonth() + 1).padStart(2, '0');
    const day = String(local.getDate()).padStart(2, '0');
    setComposerSchedule(`${year}-${month}-${day}T09:00`);
    setTab('compose');
  };

  const togglePlatform = (p: MarketingPlatform) => {
    setComposerPlatforms((prev) =>
      prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p],
    );
  };

  const handleMediaUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const toUpload = Array.from(files);
    for (const file of toUpload) {
      if (file.size > MAX_FILE_SIZE) {
        setError(`Le fichier "${file.name}" dépasse 10 Mo`);
        return;
      }
    }
    setMediaUploading(true);
    setError(null);
    try {
      for (const file of toUpload) {
        const result = await marketingApi.uploadMedia(file);
        setComposerMediaUrls((prev) => [...prev, result.url]);
      }
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      setError(err.response?.data?.error || 'Erreur lors de l\'upload du média');
    } finally {
      setMediaUploading(false);
    }
  };

  const removeMedia = (index: number) => {
    setComposerMediaUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSavePost = async () => {
    if (!composerContent.trim()) {
      setError('Le contenu est obligatoire');
      return;
    }
    if (composerPlatforms.length === 0) {
      setError('Sélectionnez au moins une plateforme');
      return;
    }
    setComposerSaving(true);
    setError(null);
    try {
      const payload = {
        title: composerTitle || undefined,
        content: composerContent,
        platforms: composerPlatforms,
        scheduledAt: composerSchedule ? new Date(composerSchedule).toISOString() : null,
        mediaUrls: composerMediaUrls.length > 0 ? composerMediaUrls : undefined,
      };
      if (editingPost) {
        await marketingApi.updatePost(editingPost.id, payload);
      } else {
        await marketingApi.createPost(payload);
      }
      resetComposer();
      setTab('posts');
      await loadPosts();
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      setError(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setComposerSaving(false);
    }
  };

  const handleAiGenerate = async () => {
    if (!aiTheme.trim()) {
      setError('Saisissez un thème pour la génération IA');
      return;
    }
    if (composerPlatforms.length === 0) {
      setError('Sélectionnez au moins une plateforme');
      return;
    }
    setAiLoading(true);
    setError(null);
    try {
      const result = await marketingApi.generatePost({
        theme: aiTheme,
        platforms: composerPlatforms,
        tone: aiTone,
        language: aiLanguage,
        withHashtags: true,
        contentType: aiContentType,
        callToAction: aiCta || undefined,
        targetAudience: aiTargetAudience || undefined,
        moduleKey: aiModuleKey || undefined,
      });
      const hashtags = result.hashtags.length ? '\n\n' + result.hashtags.map((h) => `#${h}`).join(' ') : '';
      setComposerContent(result.content + hashtags);
      if (result.title && !composerTitle) setComposerTitle(result.title);
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      setError(err.response?.data?.error || 'Erreur de génération IA');
    } finally {
      setAiLoading(false);
    }
  };

  const handlePublish = async (id: string) => {
    if (!confirm('Publier maintenant sur les réseaux sélectionnés ?')) return;
    setPublishingId(id);
    try {
      await marketingApi.publishPost(id);
      await loadPosts();
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      alert(err.response?.data?.error || 'Erreur de publication');
    } finally {
      setPublishingId(null);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Supprimer ce post ?')) return;
    await marketingApi.deletePost(id);
    await loadPosts();
  };

  const handleRefreshFeatures = async () => {
    setLoading(true);
    try {
      const result = await marketingApi.refreshFeatures();
      alert(`${result.count} fonctionnalités rafraîchies depuis trackfleet.lu`);
      await loadFeatures();
    } catch {
      alert('Erreur lors du rafraîchissement');
    } finally {
      setLoading(false);
    }
  };

  const openFeatureScrape = () => {
    setFeatureScrapeUrl('');
    setFeatureScrapeResults([]);
    setFeatureScrapeSelected(new Set());
    setFeatureScrapeOpen(true);
  };

  const handleScrapePreview = async () => {
    if (!featureScrapeUrl.trim()) return;
    setFeatureScrapeLoading(true);
    try {
      const result = await marketingApi.scrapeFeaturesPreview(featureScrapeUrl.trim());
      setFeatureScrapeResults(result.features);
      // Pre-select all non-existing
      setFeatureScrapeSelected(
        new Set(result.features.filter((f) => !f.exists).map((f) => f.slug)),
      );
      if (result.features.length === 0) {
        alert(result.message || 'Aucune fonctionnalité détectée sur cette URL');
      }
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      alert(err.response?.data?.error || 'Erreur de scraping');
    } finally {
      setFeatureScrapeLoading(false);
    }
  };

  const handleScrapeImport = async () => {
    const toImport = featureScrapeResults.filter(
      (f) => featureScrapeSelected.has(f.slug) && !f.exists,
    );
    if (toImport.length === 0) {
      alert('Aucune fonctionnalité sélectionnée');
      return;
    }
    setFeatureScrapeLoading(true);
    try {
      const result = await marketingApi.bulkCreateFeatures(
        toImport.map((f) => ({
          title: f.title,
          description: f.description,
          category: f.category ?? null,
          sourceUrl: f.sourceUrl ?? null,
        })),
      );
      alert(`${result.created} ajoutée(s), ${result.skipped} ignorée(s)`);
      setFeatureScrapeOpen(false);
      await loadFeatures();
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      alert(err.response?.data?.error || 'Erreur lors de l\'import');
    } finally {
      setFeatureScrapeLoading(false);
    }
  };

  const openFeatureForm = (f?: MarketingFeature) => {
    if (f) {
      setFeatureFormEditingId(f.id);
      setFeatureFormTitle(f.title);
      setFeatureFormDescription(f.description);
      setFeatureFormCategory(f.category || '');
      setFeatureFormSourceUrl(f.sourceUrl || '');
    } else {
      setFeatureFormEditingId(null);
      setFeatureFormTitle('');
      setFeatureFormDescription('');
      setFeatureFormCategory('');
      setFeatureFormSourceUrl('');
    }
    setFeatureFormOpen(true);
  };

  const handleFeatureFormSave = async () => {
    if (!featureFormTitle.trim() || featureFormDescription.trim().length < 10) {
      alert('Titre obligatoire et description ≥ 10 caractères');
      return;
    }
    setFeatureFormSaving(true);
    try {
      const payload = {
        title: featureFormTitle.trim(),
        description: featureFormDescription.trim(),
        category: featureFormCategory.trim() || null,
        sourceUrl: featureFormSourceUrl.trim() || null,
      };
      if (featureFormEditingId) {
        await marketingApi.updateFeature(featureFormEditingId, payload);
      } else {
        await marketingApi.createFeature(payload);
      }
      setFeatureFormOpen(false);
      await loadFeatures();
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      alert(err.response?.data?.error || 'Erreur lors de l\'enregistrement');
    } finally {
      setFeatureFormSaving(false);
    }
  };

  const handleDeleteFeature = async (id: string, title: string) => {
    if (!confirm(`Supprimer la fonctionnalité "${title}" ?`)) return;
    try {
      await marketingApi.deleteFeature(id);
      await loadFeatures();
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      alert(err.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const handleOAuthConnect = (platform: MarketingPlatform) => {
    const token = useAuthStore.getState().accessToken;
    if (!token) {
      setError('Session expirée, veuillez vous reconnecter');
      return;
    }
    const routes: Record<MarketingPlatform, string> = {
      LINKEDIN: '/api/admin/marketing/accounts/linkedin/connect',
      FACEBOOK: '/api/admin/marketing/accounts/facebook/connect',
      INSTAGRAM: '/api/admin/marketing/accounts/instagram/connect',
    };
    window.location.href = `${routes[platform]}?token=${encodeURIComponent(token)}`;
  };

  // Visual calendar: compute week days and filter posts
  const calendarWeekDays = useMemo(() => {
    const days: Date[] = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(calendarWeekStart);
      d.setDate(d.getDate() + i);
      days.push(d);
    }
    return days;
  }, [calendarWeekStart]);

  const calendarPostsByDay = useMemo(() => {
    const result: Map<number, MarketingPost[]> = new Map();
    for (let i = 0; i < 7; i++) {
      result.set(i, []);
    }
    for (const post of calendarAllPosts) {
      const postDate = post.scheduledAt ? new Date(post.scheduledAt) : post.publishedAt ? new Date(post.publishedAt) : null;
      if (!postDate) continue;
      for (let i = 0; i < 7; i++) {
        if (isSameDay(postDate, calendarWeekDays[i])) {
          result.get(i)!.push(post);
          break;
        }
      }
    }
    // Sort each day's posts by time
    for (const [, dayPosts] of result) {
      dayPosts.sort((a, b) => {
        const dateA = a.scheduledAt || a.publishedAt || a.createdAt;
        const dateB = b.scheduledAt || b.publishedAt || b.createdAt;
        return new Date(dateA).getTime() - new Date(dateB).getTime();
      });
    }
    return result;
  }, [calendarAllPosts, calendarWeekDays]);

  const navigateWeek = (offset: number) => {
    setCalendarWeekStart((prev) => {
      const d = new Date(prev);
      d.setDate(d.getDate() + offset * 7);
      return d;
    });
  };

  const goToThisWeek = () => {
    setCalendarWeekStart(getMonday(new Date()));
  };

  // Check if account token is expiring or needs reconnect
  const getAccountWarning = (acc: MarketingAccount): { type: 'expiring' | 'reconnect'; message: string } | null => {
    if (acc.metadata && (acc.metadata as Record<string, unknown>).needsReconnect === true) {
      return { type: 'reconnect', message: 'Reconnexion nécessaire' };
    }
    if (acc.expiresAt) {
      const expiresAt = new Date(acc.expiresAt);
      const now = new Date();
      const sevenDays = 7 * 24 * 60 * 60 * 1000;
      if (expiresAt.getTime() < now.getTime()) {
        return { type: 'reconnect', message: 'Token expiré' };
      }
      if (expiresAt.getTime() - now.getTime() < sevenDays) {
        return { type: 'expiring', message: 'Token expire bientôt' };
      }
    }
    return null;
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-primary-500 text-white p-3 rounded-lg">
          <Megaphone className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Marketing Digital</h1>
          <p className="text-sm text-gray-500">
            Composez et publiez du contenu sur LinkedIn, Facebook et Instagram
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-1">
          {[
            { id: 'posts' as Tab, label: 'Posts', icon: FileText },
            { id: 'compose' as Tab, label: editingPost ? 'Éditer' : 'Composer', icon: Edit3 },
            { id: 'calendar' as Tab, label: 'Calendrier', icon: CalendarDays },
            { id: 'features' as Tab, label: 'Thèmes & fonctionnalités', icon: Sparkles },
            { id: 'accounts' as Tab, label: 'Comptes connectés', icon: Linkedin },
          ].map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`px-4 py-2 flex items-center gap-2 text-sm font-medium border-b-2 transition-colors ${
                  tab === t.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </nav>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {successMsg && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4" />
          {successMsg}
          <button onClick={() => setSuccessMsg(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* POSTS LIST */}
      {tab === 'posts' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Tous les posts</h2>
            <button
              onClick={() => {
                resetComposer();
                setTab('compose');
              }}
              className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm"
            >
              <Plus className="w-4 h-4" /> Nouveau post
            </button>
          </div>
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
              <Megaphone className="w-12 h-12 mx-auto text-gray-300 mb-2" />
              <p className="text-gray-500">Aucun post pour le moment</p>
            </div>
          ) : (
            <div className="space-y-3">
              {posts.map((p) => (
                <div
                  key={p.id}
                  className={`relative bg-white border rounded-lg p-4 hover:shadow-sm transition-shadow ${
                    publishingId === p.id ? 'border-primary-300 ring-2 ring-primary-100' : 'border-gray-200'
                  }`}
                >
                  {publishingId === p.id && (
                    <div className="absolute top-2 right-2 flex items-center gap-1.5 text-xs font-medium text-primary-700 bg-primary-50 border border-primary-200 px-2 py-1 rounded-full">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Publication en cours…
                    </div>
                  )}
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span
                          className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_COLORS[p.status]}`}
                        >
                          {STATUS_LABELS[p.status]}
                        </span>
                        {p.aiGenerated && (
                          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 flex items-center gap-1">
                            <Sparkles className="w-3 h-3" /> IA
                          </span>
                        )}
                        {p.platforms.map((pl) => {
                          const platform = PLATFORMS.find((x) => x.value === pl)!;
                          const Icon = platform.icon;
                          return (
                            <span
                              key={pl}
                              className="flex items-center gap-1 text-xs text-gray-600"
                              title={platform.label}
                            >
                              <Icon className="w-3.5 h-3.5" style={{ color: platform.color }} />
                            </span>
                          );
                        })}
                        {p.scheduledAt && p.status === 'SCHEDULED' && (
                          <span className="text-xs text-blue-600 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Programmé pour le {new Date(p.scheduledAt).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
                          </span>
                        )}
                        {p.publishedAt && (p.status === 'PUBLISHED' || p.status === 'PARTIAL') && (
                          <span className="text-xs text-green-700 flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" />
                            Publié le {new Date(p.publishedAt).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
                          </span>
                        )}
                      </div>
                      {p.title && <h3 className="font-semibold text-gray-900 mb-1">{p.title}</h3>}
                      <p className="text-sm text-gray-600 line-clamp-3 whitespace-pre-wrap">{p.content}</p>
                      {p.mediaUrls && p.mediaUrls.length > 0 && (
                        <div className="flex gap-1.5 mt-2">
                          {p.mediaUrls.slice(0, 4).map((url, i) => (
                            <div key={i} className="w-12 h-12 rounded border border-gray-200 overflow-hidden bg-gray-100 shrink-0">
                              {isVideoUrl(url) ? (
                                <div className="w-full h-full flex items-center justify-center bg-gray-800">
                                  <Play className="w-4 h-4 text-white" />
                                </div>
                              ) : (
                                <img src={url} alt="" className="w-full h-full object-cover" />
                              )}
                            </div>
                          ))}
                          {p.mediaUrls.length > 4 && (
                            <div className="w-12 h-12 rounded border border-gray-200 bg-gray-100 flex items-center justify-center text-xs text-gray-500">
                              +{p.mediaUrls.length - 4}
                            </div>
                          )}
                        </div>
                      )}
                      {p.publications && p.publications.length > 0 && (
                        <div className="mt-3 border-t border-gray-100 pt-2 space-y-1">
                          {p.publications.map((pub) => {
                            const platform = PLATFORMS.find((x) => x.value === pub.platform);
                            if (!platform) return null;
                            const Icon = platform.icon;
                            return (
                              <div key={pub.id} className="flex items-center gap-2 text-xs flex-wrap">
                                <span className="flex items-center gap-1 font-medium text-gray-700 min-w-[90px]">
                                  <Icon className="w-3.5 h-3.5" style={{ color: platform.color }} />
                                  {platform.label}
                                </span>
                                {pub.status === 'SUCCESS' && (
                                  <>
                                    <span className="text-green-700 flex items-center gap-1">
                                      <CheckCircle2 className="w-3 h-3" />
                                      Publié{pub.publishedAt && ` le ${new Date(pub.publishedAt).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}`}
                                    </span>
                                    {pub.externalUrl && (
                                      <a
                                        href={pub.externalUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                                      >
                                        <Link2 className="w-3 h-3" /> Voir
                                      </a>
                                    )}
                                  </>
                                )}
                                {pub.status === 'FAILED' && (
                                  <span className="text-red-600 flex items-start gap-1">
                                    <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
                                    <span>Échec{pub.error ? ` — ${pub.error}` : ''}</span>
                                  </span>
                                )}
                                {pub.status === 'PENDING' && (
                                  <span className="text-gray-500 flex items-center gap-1">
                                    <Clock className="w-3 h-3" /> En attente
                                  </span>
                                )}
                              </div>
                            );
                          })}
                          {p.platforms
                            .filter((pl) => !p.publications.some((pub) => pub.platform === pl))
                            .map((pl) => {
                              const platform = PLATFORMS.find((x) => x.value === pl);
                              if (!platform) return null;
                              const Icon = platform.icon;
                              return (
                                <div key={pl} className="flex items-center gap-2 text-xs flex-wrap">
                                  <span className="flex items-center gap-1 font-medium text-gray-700 min-w-[90px]">
                                    <Icon className="w-3.5 h-3.5" style={{ color: platform.color }} />
                                    {platform.label}
                                  </span>
                                  <span className="text-gray-400 flex items-center gap-1">
                                    <AlertTriangle className="w-3 h-3" /> Non publié
                                  </span>
                                </div>
                              );
                            })}
                        </div>
                      )}
                      {p.errorMessage && (!p.publications || p.publications.length === 0) && (
                        <p className="mt-2 text-xs text-red-600">{p.errorMessage}</p>
                      )}
                    </div>
                    <div className="flex flex-col gap-1 shrink-0">
                      {p.status !== 'PUBLISHED' && (
                        <button
                          onClick={() => handlePublish(p.id)}
                          disabled={publishingId !== null}
                          className="text-xs px-3 py-1.5 bg-green-50 hover:bg-green-100 text-green-700 rounded flex items-center gap-1 disabled:opacity-60 disabled:cursor-not-allowed"
                          title={publishingId === p.id ? 'Publication en cours…' : 'Publier maintenant'}
                        >
                          {publishingId === p.id ? (
                            <>
                              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Envoi…
                            </>
                          ) : (
                            <>
                              <Send className="w-3.5 h-3.5" /> Publier
                            </>
                          )}
                        </button>
                      )}
                      {p.status !== 'PUBLISHED' && (
                        <button
                          onClick={() => openComposerForEdit(p)}
                          disabled={publishingId === p.id}
                          className="text-xs px-3 py-1.5 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded flex items-center gap-1 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          <Edit3 className="w-3.5 h-3.5" /> Éditer
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(p.id)}
                        disabled={publishingId === p.id}
                        className="text-xs px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-700 rounded flex items-center gap-1 disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Supprimer
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* COMPOSE */}
      {tab === 'compose' && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {editingPost ? 'Éditer le post' : 'Nouveau post'}
          </h2>

          {/* Platforms */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Plateformes</label>
            <div className="flex gap-2">
              {PLATFORMS.map((p) => {
                const Icon = p.icon;
                const active = composerPlatforms.includes(p.value);
                return (
                  <button
                    key={p.value}
                    type="button"
                    onClick={() => togglePlatform(p.value)}
                    className={`px-4 py-2 rounded-lg border-2 flex items-center gap-2 text-sm font-medium transition-colors ${
                      active
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-4 h-4" style={{ color: p.color }} />
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* AI Assist */}
          <div className="mb-4 bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-purple-600" />
              <span className="text-sm font-semibold text-purple-900">Génération assistée par IA (Claude)</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
              {themeModules.length > 0 && (
                <div className="md:col-span-3 bg-white/70 border border-purple-200 rounded p-2">
                  <select
                    value={aiModuleKey}
                    onChange={(e) => {
                      const key = e.target.value as MarketingThemeModuleKey | '';
                      setAiModuleKey(key);
                      const m = themeModules.find((x) => x.key === key);
                      if (m && !aiTargetAudience && m.audiences[0]) setAiTargetAudience(m.audiences[0]);
                    }}
                    className="w-full px-3 py-2 border border-purple-300 bg-white rounded text-sm text-purple-900"
                    title="Choisir un module TrackFleet : l'IA reçoit ses fonctionnalités réelles en contexte et propose des angles prêts à l'emploi"
                  >
                    <option value="">🧩 Thème par module TrackFleet (tournées, livraisons, VE, maintenance…)</option>
                    {themeModules.map((m) => (
                      <option key={m.key} value={m.key}>
                        {m.emoji} {m.label} — {m.tagline}
                      </option>
                    ))}
                  </select>
                  {aiModule && (
                    <>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {aiModule.suggestions.map((sg) => {
                          const active = aiTheme === sg.theme;
                          return (
                            <button
                              key={sg.title}
                              type="button"
                              onClick={() => {
                                setAiTheme(sg.theme);
                                setAiContentType(sg.contentType);
                                if (sg.audience) setAiTargetAudience(sg.audience);
                              }}
                              title={sg.theme}
                              className={`px-2.5 py-1 rounded-full border text-xs transition-colors ${
                                active
                                  ? 'bg-purple-600 border-purple-600 text-white'
                                  : 'bg-white border-purple-300 text-purple-800 hover:bg-purple-100'
                              }`}
                            >
                              {sg.title}
                            </button>
                          );
                        })}
                      </div>
                      <p className="text-[11px] text-purple-700 mt-1.5">
                        Contexte injecté à l'IA : {aiModule.facts.join(' · ')}
                      </p>
                    </>
                  )}
                </div>
              )}
              {features.length > 0 && (
                <select
                  value=""
                  onChange={(e) => {
                    const f = features.find((x) => x.id === e.target.value);
                    if (f) {
                      setAiTheme(`${f.title} — ${f.description}`);
                    }
                  }}
                  className="md:col-span-3 px-3 py-2 border border-purple-300 bg-white rounded text-sm text-purple-900"
                  title="Pré-remplir le thème à partir d'une fonctionnalité scrapée"
                >
                  <option value="">✨ Inspirer depuis une fonctionnalité TrackFleet…</option>
                  {features.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.category ? `[${f.category}] ` : ''}
                      {f.title}
                    </option>
                  ))}
                </select>
              )}
              <input
                type="text"
                placeholder="Thème (ex: trackers Teltonika, géofencing, ROI flotte)"
                value={aiTheme}
                onChange={(e) => setAiTheme(e.target.value)}
                className="md:col-span-3 px-3 py-2 border border-gray-300 rounded text-sm"
              />
              <select
                value={aiContentType}
                onChange={(e) => setAiContentType(e.target.value as MarketingContentType)}
                className="px-3 py-2 border border-gray-300 rounded text-sm"
              >
                <option value="product-highlight">Type : Mise en avant produit</option>
                <option value="tip">Type : Conseil pratique</option>
                <option value="case-study">Type : Étude de cas</option>
                <option value="industry-news">Type : Actualité secteur</option>
                <option value="question">Type : Question engageante</option>
                <option value="testimonial">Type : Témoignage</option>
                <option value="comparison">Type : Comparaison avant/après</option>
                <option value="behind-the-scenes">Type : Coulisses</option>
              </select>
              <select
                value={aiTone}
                onChange={(e) => setAiTone(e.target.value as typeof aiTone)}
                className="px-3 py-2 border border-gray-300 rounded text-sm"
              >
                <option value="professional">Ton : Professionnel</option>
                <option value="casual">Ton : Décontracté</option>
                <option value="enthusiastic">Ton : Enthousiaste</option>
                <option value="technical">Ton : Technique</option>
              </select>
              <select
                value={aiLanguage}
                onChange={(e) => setAiLanguage(e.target.value as typeof aiLanguage)}
                className="px-3 py-2 border border-gray-300 rounded text-sm"
              >
                <option value="fr">Langue : Français</option>
                <option value="en">Langue : English</option>
                <option value="de">Langue : Deutsch</option>
              </select>
              <select
                value={aiTargetAudience}
                onChange={(e) => setAiTargetAudience(e.target.value as MarketingTargetAudience | '')}
                className="px-3 py-2 border border-gray-300 rounded text-sm"
              >
                <option value="">Audience : Tous</option>
                <option value="fleet-manager">Audience : Gestionnaire de flotte</option>
                <option value="transporter">Audience : Transporteur</option>
                <option value="artisan">Audience : Artisan / TPE</option>
                <option value="pme-director">Audience : Directeur PME</option>
                <option value="logistics">Audience : Responsable logistique</option>
              </select>
              <input
                type="text"
                placeholder="CTA personnalisé (ex: Demandez une démo)"
                value={aiCta}
                onChange={(e) => setAiCta(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded text-sm"
              />
              <button
                type="button"
                onClick={handleAiGenerate}
                disabled={aiLoading || !aiTheme.trim()}
                className="bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 text-white px-4 py-2 rounded text-sm flex items-center justify-center gap-2"
              >
                {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                Générer
              </button>
            </div>
            <p className="text-xs text-purple-700">
              L'IA reçoit automatiquement le catalogue de fonctionnalités scrapées depuis trackfleet.lu en contexte
              pour ne rien inventer. Choisis un module (tournées, livraisons, véhicules électriques, maintenance…)
              pour obtenir des angles prêts à l'emploi et injecter ses fonctionnalités réelles dans le prompt, ou
              pré-remplis le thème depuis une fonctionnalité, ou tape le tien librement. Tu pourras éditer le résultat ensuite.
            </p>
          </div>

          {/* Calendar Generator */}
          <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-semibold text-blue-900">Calendrier de contenu (5 posts)</span>
            </div>
            <div className="flex flex-col md:flex-row gap-2 mb-2">
              {themeModules.length > 0 && (
                <select
                  value={calendarModuleKey}
                  onChange={(e) => {
                    const key = e.target.value as MarketingThemeModuleKey | '';
                    setCalendarModuleKey(key);
                    const m = themeModules.find((x) => x.key === key);
                    if (m) setCalendarTheme(`${m.label} — ${m.tagline}`);
                  }}
                  className="md:w-72 px-3 py-2 border border-blue-300 bg-white rounded text-sm text-blue-900"
                  title="Semaine thématique sur un module TrackFleet : pré-remplit le thème et injecte les fonctionnalités réelles du module"
                >
                  <option value="">🧩 Module (optionnel)</option>
                  {themeModules.map((m) => (
                    <option key={m.key} value={m.key}>
                      {m.emoji} {m.label}
                    </option>
                  ))}
                </select>
              )}
              <input
                type="text"
                placeholder="Thème de la semaine (ex: sécurité des véhicules, économies carburant)"
                value={calendarTheme}
                onChange={(e) => setCalendarTheme(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm"
              />
              <button
                type="button"
                onClick={async () => {
                  if (!calendarTheme.trim()) { setError('Saisissez un thème pour le calendrier'); return; }
                  setCalendarLoading(true);
                  setError(null);
                  setCalendarPosts([]);
                  setCalendarSelected(new Set());
                  try {
                    const result = await marketingApi.generateCalendar({
                      weekTheme: calendarTheme,
                      numberOfPosts: 5,
                      platforms: composerPlatforms.length > 0 ? composerPlatforms : undefined,
                      language: aiLanguage,
                      targetAudience: aiTargetAudience || undefined,
                      moduleKey: calendarModuleKey || undefined,
                    });
                    setCalendarPosts(result);
                    setCalendarSelected(new Set(result.map((_, i) => i)));
                  } catch (e) {
                    const err = e as { response?: { data?: { error?: string } } };
                    setError(err.response?.data?.error || 'Erreur de génération du calendrier');
                  } finally {
                    setCalendarLoading(false);
                  }
                }}
                disabled={calendarLoading || !calendarTheme.trim()}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white px-4 py-2 rounded text-sm flex items-center gap-2 whitespace-nowrap"
              >
                {calendarLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calendar className="w-4 h-4" />}
                Générer calendrier
              </button>
            </div>

            {calendarPosts.length > 0 && (
              <div className="mt-3 space-y-2">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-blue-900">{calendarPosts.length} posts générés</span>
                  <button
                    type="button"
                    onClick={async () => {
                      const selected = calendarPosts.filter((_, i) => calendarSelected.has(i));
                      if (selected.length === 0) { setError('Sélectionnez au moins un post'); return; }
                      setComposerSaving(true);
                      setError(null);
                      try {
                        for (const post of selected) {
                          const hashtags = post.hashtags.length
                            ? '\n\n' + post.hashtags.map((h) => `#${h}`).join(' ')
                            : '';
                          await marketingApi.createPost({
                            title: post.title || undefined,
                            content: post.content + hashtags,
                            platforms: post.suggestedPlatforms,
                            aiGenerated: true,
                            theme: calendarTheme,
                          });
                        }
                        setCalendarPosts([]);
                        setCalendarSelected(new Set());
                        setCalendarTheme('');
                        setCalendarModuleKey('');
                        setTab('posts');
                        await loadPosts();
                      } catch (e) {
                        const err = e as { response?: { data?: { error?: string } } };
                        setError(err.response?.data?.error || 'Erreur lors de la sauvegarde');
                      } finally {
                        setComposerSaving(false);
                      }
                    }}
                    disabled={composerSaving || calendarSelected.size === 0}
                    className="bg-green-600 hover:bg-green-700 disabled:bg-green-300 text-white px-3 py-1.5 rounded text-xs flex items-center gap-1"
                  >
                    {composerSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                    Sauvegarder {calendarSelected.size} brouillon(s)
                  </button>
                </div>
                {calendarPosts.map((post, idx) => {
                  const isSelected = calendarSelected.has(idx);
                  const platformInfo = PLATFORMS.find((p) => post.suggestedPlatforms.includes(p.value));
                  const engagementColors: Record<string, string> = {
                    low: 'bg-gray-100 text-gray-600',
                    medium: 'bg-yellow-100 text-yellow-700',
                    high: 'bg-green-100 text-green-700',
                  };
                  return (
                    <div
                      key={idx}
                      onClick={() => {
                        setCalendarSelected((prev) => {
                          const next = new Set(prev);
                          if (next.has(idx)) next.delete(idx);
                          else next.add(idx);
                          return next;
                        });
                      }}
                      className={`cursor-pointer rounded-lg border-2 p-3 transition-colors ${
                        isSelected
                          ? 'border-blue-400 bg-white'
                          : 'border-gray-200 bg-gray-50 opacity-60'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          readOnly
                          className="w-4 h-4 rounded text-blue-600"
                        />
                        <span className="text-xs font-semibold text-gray-900">{post.title}</span>
                        {platformInfo && (
                          <span className="text-xs text-gray-500">
                            {post.suggestedPlatforms.join(', ')}
                          </span>
                        )}
                        <span className={`text-xs px-1.5 py-0.5 rounded ${engagementColors[post.estimatedEngagement]}`}>
                          {post.estimatedEngagement}
                        </span>
                        {post.bestPostingTime && (
                          <span className="text-xs text-gray-500 flex items-center gap-0.5">
                            <Clock className="w-3 h-3" /> {post.bestPostingTime}
                          </span>
                        )}
                        <span className="text-xs bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded">
                          {post.contentType}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 line-clamp-2 whitespace-pre-wrap ml-6">
                        {post.content}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Title (optional) */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Titre interne (optionnel)
            </label>
            <input
              type="text"
              value={composerTitle}
              onChange={(e) => setComposerTitle(e.target.value)}
              placeholder="Pour s'y retrouver dans la liste — non publié"
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
            />
          </div>

          {/* Content */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Contenu du post <span className="text-gray-400">({composerContent.length} car.)</span>
            </label>
            <textarea
              value={composerContent}
              onChange={(e) => setComposerContent(e.target.value)}
              rows={12}
              placeholder="Rédige ton post ici, ou utilise la génération IA ci-dessus…"
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm font-mono"
            />
          </div>

          {/* Media Upload */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
              <ImageIcon className="w-4 h-4" />
              Médias
            </label>

            {/* Thumbnails */}
            {composerMediaUrls.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {composerMediaUrls.map((url, idx) => (
                  <div key={idx} className="relative group w-24 h-24 rounded-lg overflow-hidden border border-gray-200 bg-gray-100">
                    {isVideoUrl(url) ? (
                      <div className="w-full h-full flex items-center justify-center bg-gray-800">
                        <Play className="w-8 h-8 text-white" />
                      </div>
                    ) : (
                      <img src={url} alt="" className="w-full h-full object-cover" />
                    )}
                    <button
                      type="button"
                      onClick={() => removeMedia(idx)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Drop zone / upload area */}
            <div
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleMediaUpload(e.dataTransfer.files);
              }}
              className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-primary-400 hover:bg-primary-50/30 transition-colors"
            >
              {mediaUploading ? (
                <div className="flex items-center justify-center gap-2 text-gray-500">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span className="text-sm">Upload en cours...</span>
                </div>
              ) : (
                <>
                  <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                  <p className="text-sm text-gray-600">
                    Glissez-déposez ou <span className="text-primary-600 font-medium">cliquez pour ajouter</span>
                  </p>
                  <p className="text-xs text-gray-400 mt-1">JPG, PNG, GIF, WebP, MP4 - max 10 Mo par fichier</p>
                </>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_MEDIA_TYPES}
              multiple
              className="hidden"
              onChange={(e) => handleMediaUpload(e.target.files)}
            />
          </div>

          {/* Schedule */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              Programmer (optionnel — sinon brouillon)
            </label>
            <input
              type="datetime-local"
              value={composerSchedule}
              onChange={(e) => setComposerSchedule(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded text-sm"
            />
          </div>

          <div className="flex gap-2 justify-end">
            <button
              onClick={() => {
                resetComposer();
                setTab('posts');
              }}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
            >
              Annuler
            </button>
            <button
              onClick={handleSavePost}
              disabled={composerSaving}
              className="bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white px-6 py-2 rounded-lg flex items-center gap-2 text-sm font-medium"
            >
              {composerSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              {editingPost ? 'Mettre à jour' : 'Enregistrer'}
            </button>
          </div>
        </div>
      )}

      {/* VISUAL CALENDAR */}
      {tab === 'calendar' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Calendrier des publications</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigateWeek(-1)}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
                title="Semaine précédente"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={goToThisWeek}
                className="px-3 py-1.5 text-sm font-medium text-primary-600 hover:bg-primary-50 rounded-lg"
              >
                Cette semaine
              </button>
              <button
                onClick={() => navigateWeek(1)}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
                title="Semaine suivante"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>

          {calendarViewLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : (
            <div className="grid grid-cols-7 gap-2">
              {calendarWeekDays.map((day, dayIdx) => {
                const dayPosts = calendarPostsByDay.get(dayIdx) || [];
                const today = isToday(day);
                return (
                  <div
                    key={dayIdx}
                    className={`rounded-lg border min-h-[200px] flex flex-col ${
                      today
                        ? 'border-primary-300 bg-primary-50/30'
                        : 'border-gray-200 bg-white'
                    }`}
                  >
                    {/* Day header */}
                    <div className={`px-2 py-1.5 text-xs font-semibold border-b ${
                      today
                        ? 'text-primary-700 border-primary-200 bg-primary-50'
                        : 'text-gray-600 border-gray-100 bg-gray-50'
                    }`}>
                      {formatDayHeader(day, dayIdx)}
                    </div>

                    {/* Posts */}
                    <div className="flex-1 p-1 space-y-1">
                      {dayPosts.map((post) => {
                        const postDate = post.scheduledAt || post.publishedAt || post.createdAt;
                        const time = new Date(postDate).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
                        return (
                          <button
                            key={post.id}
                            onClick={() => openComposerForEdit(post)}
                            className="w-full text-left p-1.5 rounded border border-gray-100 hover:border-gray-300 hover:shadow-sm transition-all bg-white cursor-pointer"
                          >
                            <div className="flex items-center gap-1 mb-0.5">
                              {post.platforms.map((pl) => {
                                const platform = PLATFORMS.find((x) => x.value === pl)!;
                                const PIcon = platform.icon;
                                return <PIcon key={pl} className="w-3 h-3" style={{ color: platform.color }} />;
                              })}
                              <span className={`text-[10px] font-semibold px-1 py-0 rounded ${STATUS_COLORS[post.status]}`}>
                                {STATUS_LABELS[post.status]}
                              </span>
                            </div>
                            <p className="text-[11px] text-gray-800 line-clamp-2 leading-tight">
                              {post.title || post.content.slice(0, 50)}
                            </p>
                            <span className="text-[10px] text-gray-400">{time}</span>
                          </button>
                        );
                      })}
                    </div>

                    {/* Add button */}
                    <div className="p-1 border-t border-gray-100">
                      <button
                        onClick={() => openComposerForDate(day)}
                        className="w-full py-1 text-xs text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded flex items-center justify-center gap-1 transition-colors"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* FEATURES */}
      {tab === 'features' && (
        <div>
          <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Fonctionnalités utilisées par l'IA
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Catalogue injecté automatiquement dans chaque génération de post pour empêcher l'IA d'inventer des features.
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => openFeatureForm()}
                className="bg-primary-500 hover:bg-primary-600 text-white px-3 py-2 rounded-lg flex items-center gap-2 text-sm"
              >
                <Plus className="w-4 h-4" /> Ajouter manuellement
              </button>
              <button
                onClick={openFeatureScrape}
                className="bg-purple-500 hover:bg-purple-600 text-white px-3 py-2 rounded-lg flex items-center gap-2 text-sm"
              >
                <Link2 className="w-4 h-4" /> Scraper une URL
              </button>
              <button
                onClick={handleRefreshFeatures}
                disabled={loading}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-lg flex items-center gap-2 text-sm"
                title="Re-scraper trackfleet.lu (baseline)"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Re-scraper trackfleet.lu
              </button>
            </div>
          </div>
          {features.length === 0 && !loading && (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
              <Sparkles className="w-12 h-12 mx-auto text-gray-300 mb-2" />
              <p className="text-gray-500 mb-3">Aucune fonctionnalité en cache</p>
              <button
                onClick={handleRefreshFeatures}
                className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-lg text-sm"
              >
                Lancer le premier scraping
              </button>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {features.map((f) => (
              <div key={f.id} className="bg-white border border-gray-200 rounded-lg p-4 group">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h3 className="font-semibold text-gray-900 text-sm flex-1">{f.title}</h3>
                  {f.category && (
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded shrink-0">
                      {f.category}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-600">{f.description}</p>
                <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-100">
                  {f.sourceUrl && (
                    <a
                      href={f.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                    >
                      <Link2 className="w-3 h-3" /> Source
                    </a>
                  )}
                  <div className="flex-1" />
                  <button
                    onClick={() => openFeatureForm(f)}
                    className="text-xs px-2 py-1 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded flex items-center gap-1"
                  >
                    <Edit3 className="w-3 h-3" /> Éditer
                  </button>
                  <button
                    onClick={() => handleDeleteFeature(f.id, f.title)}
                    className="text-xs px-2 py-1 bg-red-50 hover:bg-red-100 text-red-700 rounded flex items-center gap-1"
                  >
                    <Trash2 className="w-3 h-3" /> Supprimer
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SCRAPE URL MODAL */}
      {featureScrapeOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Link2 className="w-4 h-4 text-purple-600" /> Scraper une URL
              </h3>
              <button
                onClick={() => setFeatureScrapeOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              <div className="flex gap-2 mb-3">
                <input
                  type="url"
                  placeholder="https://exemple.com/fonctionnalites"
                  value={featureScrapeUrl}
                  onChange={(e) => setFeatureScrapeUrl(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleScrapePreview()}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm"
                />
                <button
                  onClick={handleScrapePreview}
                  disabled={featureScrapeLoading || !featureScrapeUrl.trim()}
                  className="bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 text-white px-4 py-2 rounded text-sm flex items-center gap-2"
                >
                  {featureScrapeLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                  Analyser
                </button>
              </div>
              <p className="text-xs text-gray-500 mb-3">
                Le scraper extrait les blocs <code>h1/h2/h3 + paragraphe</code> de la page. Coche
                celles que tu veux ajouter à ton catalogue. Les fonctionnalités déjà connues sont
                désactivées.
              </p>
              {featureScrapeResults.length > 0 && (
                <div className="space-y-2">
                  {featureScrapeResults.map((f) => {
                    const checked = featureScrapeSelected.has(f.slug);
                    return (
                      <label
                        key={f.slug}
                        className={`flex items-start gap-3 p-3 border rounded cursor-pointer transition-colors ${
                          f.exists
                            ? 'bg-gray-50 border-gray-200 cursor-not-allowed opacity-60'
                            : checked
                              ? 'bg-purple-50 border-purple-300'
                              : 'bg-white border-gray-200 hover:border-purple-200'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={f.exists}
                          onChange={(e) => {
                            const next = new Set(featureScrapeSelected);
                            if (e.target.checked) next.add(f.slug);
                            else next.delete(f.slug);
                            setFeatureScrapeSelected(next);
                          }}
                          className="mt-1"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-sm text-gray-900">{f.title}</span>
                            {f.category && (
                              <span className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                                {f.category}
                              </span>
                            )}
                            {f.exists && (
                              <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                                Déjà ajoutée
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-600 mt-1 line-clamp-2">{f.description}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
            {featureScrapeResults.length > 0 && (
              <div className="p-4 border-t border-gray-200 flex items-center justify-between gap-2">
                <span className="text-xs text-gray-500">
                  {featureScrapeSelected.size} sélectionnée(s) sur{' '}
                  {featureScrapeResults.filter((f) => !f.exists).length} ajoutables
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setFeatureScrapeOpen(false)}
                    className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleScrapeImport}
                    disabled={featureScrapeLoading || featureScrapeSelected.size === 0}
                    className="bg-primary-500 hover:bg-primary-600 disabled:bg-gray-300 text-white px-4 py-2 rounded text-sm flex items-center gap-2"
                  >
                    {featureScrapeLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Plus className="w-4 h-4" />
                    )}
                    Importer la sélection
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ADD/EDIT FEATURE MODAL */}
      {featureFormOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900">
                {featureFormEditingId ? 'Éditer la fonctionnalité' : 'Nouvelle fonctionnalité'}
              </h3>
              <button
                onClick={() => setFeatureFormOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Titre *</label>
                <input
                  type="text"
                  value={featureFormTitle}
                  onChange={(e) => setFeatureFormTitle(e.target.value)}
                  placeholder="Ex: Géofencing intelligent"
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Description *</label>
                <textarea
                  value={featureFormDescription}
                  onChange={(e) => setFeatureFormDescription(e.target.value)}
                  rows={4}
                  placeholder="Décris ce que fait la fonctionnalité (l'IA s'en servira comme contexte)"
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm resize-y"
                />
                <p className="text-[10px] text-gray-400 mt-1">Minimum 10 caractères</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Catégorie</label>
                  <input
                    type="text"
                    value={featureFormCategory}
                    onChange={(e) => setFeatureFormCategory(e.target.value)}
                    placeholder="Ex: TRACKING"
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">URL source</label>
                  <input
                    type="url"
                    value={featureFormSourceUrl}
                    onChange={(e) => setFeatureFormSourceUrl(e.target.value)}
                    placeholder="https://…"
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  />
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={() => setFeatureFormOpen(false)}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
              >
                Annuler
              </button>
              <button
                onClick={handleFeatureFormSave}
                disabled={featureFormSaving}
                className="bg-primary-500 hover:bg-primary-600 disabled:bg-gray-300 text-white px-4 py-2 rounded text-sm flex items-center gap-2"
              >
                {featureFormSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {featureFormEditingId ? 'Mettre à jour' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ACCOUNTS */}
      {tab === 'accounts' && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Comptes sociaux connectés</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {PLATFORMS.map((p) => {
              const Icon = p.icon;
              const acc = accounts.find((a) => a.platform === p.value);
              const warning = acc ? getAccountWarning(acc) : null;
              const isInstagram = p.value === 'INSTAGRAM';
              return (
                <div key={p.value} className="bg-white border border-gray-200 rounded-lg p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <Icon className="w-6 h-6" style={{ color: p.color }} />
                    <h3 className="font-semibold text-gray-900">{p.label}</h3>
                  </div>
                  {acc ? (
                    <>
                      <p className="text-sm text-gray-700">{acc.accountName}</p>
                      <p className="text-xs text-gray-500 mb-2">
                        Connecté le {new Date(acc.createdAt).toLocaleDateString('fr-FR')}
                      </p>
                      {warning && (
                        <div className={`flex items-center gap-1.5 mb-2 text-xs font-medium ${
                          warning.type === 'reconnect' ? 'text-red-600' : 'text-orange-600'
                        }`}>
                          <AlertTriangle className="w-3.5 h-3.5" />
                          {warning.message}
                          {warning.type === 'reconnect' && (
                            <button
                              onClick={() => handleOAuthConnect(p.value)}
                              className="ml-1 underline hover:no-underline"
                            >
                              Reconnecter
                            </button>
                          )}
                        </div>
                      )}
                      <button
                        onClick={async () => {
                          if (!confirm('Déconnecter ce compte ?')) return;
                          await marketingApi.deleteAccount(acc.id);
                          await loadAccounts();
                        }}
                        className="text-xs text-red-600 hover:text-red-700"
                      >
                        Déconnecter
                      </button>
                    </>
                  ) : (
                    <div>
                      {isInstagram && (
                        <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                          <Link2 className="w-3 h-3" />
                          Nécessite un compte Instagram Business lié à une page Facebook
                        </p>
                      )}
                      <button
                        onClick={() => handleOAuthConnect(p.value)}
                        className="w-full bg-gray-900 hover:bg-gray-800 text-white px-4 py-2 rounded-lg text-sm flex items-center justify-center gap-2"
                      >
                        <Icon className="w-4 h-4" />
                        Connecter {p.label}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* OAuth Credentials Configuration */}
          <div className="bg-white border border-gray-200 rounded-lg">
            <button
              onClick={() => setCredentialsOpen(!credentialsOpen)}
              className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-gray-500" />
                <span className="font-semibold text-gray-900">Credentials OAuth</span>
                {Object.values(settingsStatus).some((s) => !s.configured) && (
                  <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">
                    Configuration requise
                  </span>
                )}
              </div>
              <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${credentialsOpen ? 'rotate-90' : ''}`} />
            </button>

            {credentialsOpen && (
              <div className="border-t border-gray-200 p-4 space-y-6">
                {/* LinkedIn */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Linkedin className="w-4 h-4" style={{ color: '#0A66C2' }} />
                    <h4 className="text-sm font-semibold text-gray-900">LinkedIn Developer App</h4>
                    <a href="https://www.linkedin.com/developers/apps" target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline">
                      Créer une app
                    </a>
                  </div>
                  {[
                    { key: 'LINKEDIN_CLIENT_ID', label: 'Client ID', placeholder: 'ex: 77xxxxxxxxxxxxx' },
                    { key: 'LINKEDIN_CLIENT_SECRET', label: 'Client Secret', placeholder: 'ex: AbCdEfGhIjKlMn', secret: true },
                    { key: 'LINKEDIN_REDIRECT_URI', label: 'Redirect URI', placeholder: 'https://apps.trackfleet.lu/api/admin/marketing/accounts/linkedin/callback' },
                  ].map(({ key, label, placeholder, secret }) => (
                    <div key={key} className="flex items-center gap-2 mb-2">
                      <label className="text-xs text-gray-600 w-32 shrink-0">{label}</label>
                      <div className="flex-1 relative">
                        <input
                          type={secret && !showSecrets[key] ? 'password' : 'text'}
                          value={credentialInputs[key] ?? ''}
                          onChange={(e) => setCredentialInputs((prev) => ({ ...prev, [key]: e.target.value }))}
                          placeholder={settingsStatus[key]?.configured ? (settingsMasked[key] || 'Configuré') : placeholder}
                          className={`w-full px-3 py-1.5 border rounded text-sm pr-16 ${
                            settingsStatus[key]?.configured ? 'border-green-300 bg-green-50' : 'border-gray-300'
                          }`}
                        />
                        <div className="absolute right-1 top-1 flex gap-1">
                          {secret && (
                            <button
                              type="button"
                              onClick={() => setShowSecrets((prev) => ({ ...prev, [key]: !prev[key] }))}
                              className="p-1 text-gray-400 hover:text-gray-600"
                            >
                              {showSecrets[key] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                            </button>
                          )}
                          <button
                            type="button"
                            disabled={!credentialInputs[key]?.trim() || credentialSaving === key}
                            onClick={async () => {
                              setCredentialSaving(key);
                              try {
                                await marketingApi.saveSetting(key, credentialInputs[key]);
                                setCredentialInputs((prev) => ({ ...prev, [key]: '' }));
                                await loadSettings();
                              } catch {
                                setError('Erreur lors de la sauvegarde');
                              } finally {
                                setCredentialSaving(null);
                              }
                            }}
                            className="p-1 text-primary-500 hover:text-primary-700 disabled:text-gray-300"
                          >
                            {credentialSaving === key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      </div>
                      {settingsStatus[key]?.configured && (
                        <span className="text-xs text-green-600 flex items-center gap-0.5 shrink-0">
                          <CheckCircle2 className="w-3 h-3" />
                          {settingsStatus[key]?.source === 'env' ? 'env' : 'db'}
                        </span>
                      )}
                    </div>
                  ))}
                </div>

                {/* Facebook / Instagram */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Facebook className="w-4 h-4" style={{ color: '#1877F2' }} />
                    <h4 className="text-sm font-semibold text-gray-900">Meta Developer App (Facebook + Instagram)</h4>
                    <a href="https://developers.facebook.com/apps" target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline">
                      Créer une app
                    </a>
                  </div>
                  {[
                    { key: 'FACEBOOK_APP_ID', label: 'App ID', placeholder: 'ex: 123456789012345' },
                    { key: 'FACEBOOK_APP_SECRET', label: 'App Secret', placeholder: 'ex: abc123def456...', secret: true },
                    { key: 'FACEBOOK_REDIRECT_URI', label: 'Redirect URI', placeholder: 'https://apps.trackfleet.lu/api/admin/marketing/accounts/facebook/callback' },
                  ].map(({ key, label, placeholder, secret }) => (
                    <div key={key} className="flex items-center gap-2 mb-2">
                      <label className="text-xs text-gray-600 w-32 shrink-0">{label}</label>
                      <div className="flex-1 relative">
                        <input
                          type={secret && !showSecrets[key] ? 'password' : 'text'}
                          value={credentialInputs[key] ?? ''}
                          onChange={(e) => setCredentialInputs((prev) => ({ ...prev, [key]: e.target.value }))}
                          placeholder={settingsStatus[key]?.configured ? (settingsMasked[key] || 'Configuré') : placeholder}
                          className={`w-full px-3 py-1.5 border rounded text-sm pr-16 ${
                            settingsStatus[key]?.configured ? 'border-green-300 bg-green-50' : 'border-gray-300'
                          }`}
                        />
                        <div className="absolute right-1 top-1 flex gap-1">
                          {secret && (
                            <button
                              type="button"
                              onClick={() => setShowSecrets((prev) => ({ ...prev, [key]: !prev[key] }))}
                              className="p-1 text-gray-400 hover:text-gray-600"
                            >
                              {showSecrets[key] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                            </button>
                          )}
                          <button
                            type="button"
                            disabled={!credentialInputs[key]?.trim() || credentialSaving === key}
                            onClick={async () => {
                              setCredentialSaving(key);
                              try {
                                await marketingApi.saveSetting(key, credentialInputs[key]);
                                setCredentialInputs((prev) => ({ ...prev, [key]: '' }));
                                await loadSettings();
                              } catch {
                                setError('Erreur lors de la sauvegarde');
                              } finally {
                                setCredentialSaving(null);
                              }
                            }}
                            className="p-1 text-primary-500 hover:text-primary-700 disabled:text-gray-300"
                          >
                            {credentialSaving === key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      </div>
                      {settingsStatus[key]?.configured && (
                        <span className="text-xs text-green-600 flex items-center gap-0.5 shrink-0">
                          <CheckCircle2 className="w-3 h-3" />
                          {settingsStatus[key]?.source === 'env' ? 'env' : 'db'}
                        </span>
                      )}
                    </div>
                  ))}
                </div>

                {/* Instagram (separate app) */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Instagram className="w-4 h-4" style={{ color: '#E4405F' }} />
                    <h4 className="text-sm font-semibold text-gray-900">Instagram API (app séparée)</h4>
                    <a href="https://developers.facebook.com/apps" target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline">
                      Console Meta
                    </a>
                  </div>
                  {[
                    { key: 'INSTAGRAM_APP_ID', label: 'App ID', placeholder: 'ex: 987654321098765' },
                    { key: 'INSTAGRAM_APP_SECRET', label: 'App Secret', placeholder: 'ex: xyz789abc123...', secret: true },
                    { key: 'INSTAGRAM_REDIRECT_URI', label: 'Redirect URI', placeholder: 'https://apps.trackfleet.lu/api/admin/marketing/accounts/instagram/callback' },
                  ].map(({ key, label, placeholder, secret }) => (
                    <div key={key} className="flex items-center gap-2 mb-2">
                      <label className="text-xs text-gray-600 w-32 shrink-0">{label}</label>
                      <div className="flex-1 relative">
                        <input
                          type={secret && !showSecrets[key] ? 'password' : 'text'}
                          value={credentialInputs[key] ?? ''}
                          onChange={(e) => setCredentialInputs((prev) => ({ ...prev, [key]: e.target.value }))}
                          placeholder={settingsStatus[key]?.configured ? (settingsMasked[key] || 'Configuré') : placeholder}
                          className={`w-full px-3 py-1.5 border rounded text-sm pr-16 ${
                            settingsStatus[key]?.configured ? 'border-green-300 bg-green-50' : 'border-gray-300'
                          }`}
                        />
                        <div className="absolute right-1 top-1 flex gap-1">
                          {secret && (
                            <button
                              type="button"
                              onClick={() => setShowSecrets((prev) => ({ ...prev, [key]: !prev[key] }))}
                              className="p-1 text-gray-400 hover:text-gray-600"
                            >
                              {showSecrets[key] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                            </button>
                          )}
                          <button
                            type="button"
                            disabled={!credentialInputs[key]?.trim() || credentialSaving === key}
                            onClick={async () => {
                              setCredentialSaving(key);
                              try {
                                await marketingApi.saveSetting(key, credentialInputs[key]);
                                setCredentialInputs((prev) => ({ ...prev, [key]: '' }));
                                await loadSettings();
                              } catch {
                                setError('Erreur lors de la sauvegarde');
                              } finally {
                                setCredentialSaving(null);
                              }
                            }}
                            className="p-1 text-primary-500 hover:text-primary-700 disabled:text-gray-300"
                          >
                            {credentialSaving === key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      </div>
                      {settingsStatus[key]?.configured && (
                        <span className="text-xs text-green-600 flex items-center gap-0.5 shrink-0">
                          <CheckCircle2 className="w-3 h-3" />
                          {settingsStatus[key]?.source === 'env' ? 'env' : 'db'}
                        </span>
                      )}
                    </div>
                  ))}
                </div>

                <p className="text-xs text-gray-500">
                  Les credentials sont stockées chiffrées (AES-256-GCM) en base de données. Elles sont prioritaires sur les variables d'environnement.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
